import { useEffect, useState } from "react";
import { parseISO, isBefore, differenceInMilliseconds, format } from 'date-fns';
import TitleRecordFunction from "../../components/TitleRecordFunction";
import api from '../../api/manage_database/FlowMeasurementReq';
import BackButton from "../../components/BackButton";
import Spinner from "../../components/Spinner";
import { FaTrashCan } from "react-icons/fa6";
import { FcOk } from "react-icons/fc";
import { MdCancel } from "react-icons/md";
import { toast } from 'sonner';
import SelectPlace from "../../components/SelectPlace";
import SelectThoroughfare from "../../components/SelectThroughfare";
import SelectTypeLines from "../../components/SelectTypeLines";
import DateInput from "../../components/InputDate";
import SelectParameter from "../../components/SelectParameter";
import TimeInput from "../../components/InputTime";

const FlowMeasurementSampleRecord = () => {
    const [loadingState, setLoadingState] = useState(false);
    const [flowMeters, setFlowMeters] = useState([]);
    const [selectedPlace, setSelectedPlace] = useState('');
    const [places, setPlaces] = useState([]);
    const [selectedThoroughfare, setSelectedThoroughfare] = useState('');
    const [thoroughfares, setThoroughfares] = useState([]);
    const [selectedTypeLine, setSelectedTypeLine] = useState('');
    const [typeLines, setTypeLines] = useState([]);
    const [deletingAllWithSameTime, setDeletingAllWithSameTime] = useState(false);
    const [deleteSampleIndex, setDeleteSampleIndex] = useState(null);
    const chartOrder = [
        'Flow',
        'DT',
        'TTUp',
        'TTDn',
        'FlowVelocity',
        'AbsoluteSoundVelocity',
        'SoundVelocity',
        'AbsoluteRe',
        'Re',
        'SignalAmplitudeUp',
        'SignalAmplitudeDn',
        'RxGainUp',
        'RxGainDn',
        'SNRUp',
        'SNRDn',
        'CorrelationCoef',
        'ReferenceCoef',
        'PcbTemp',
        'ExternalInput',
        'BurstsAccepted',
        'Temp',
        'StDev',
        'MeanFlow',
        'MeanDT',
        'StdFlow',
        'StdDT',
        'DiffStdFlow',
    ];
    const excludedSampleDeleteParameters = ['DiffStdFlow', 'StdDT', 'StdFlow', 'MeanDT'];
    const [chartName, setChartName] = useState(chartOrder[0]);

    const now = new Date();
    const initialDate = format(now, 'yyyy-MM-dd');
    const [selectedFromDate, setSelectedFromDate] = useState(initialDate);
    const [selectedToDate, setSelectedToDate] = useState(initialDate);
    const [selectedFromTime, setSelectedFromTime] = useState('00:00:00');
    const [selectedToTime, setSelectedToTime] = useState('00:00:01');
    const [samples, setSamples] = useState([]);

    const formatSampleDate = (dateString) => {
        if (!dateString) {
            return '-';
        }
        const parsedDate = parseISO(dateString);
        if (Number.isNaN(parsedDate.getTime())) {
            return dateString;
        }
        return format(parsedDate, 'yyyy-MM-dd HH:mm:ss.SSS');
    };

    const handleChangeFromDate = (event) => {
        setSelectedFromDate(event.target.value);
    };

    const handleChangeFromTime = (event) => {
        setSelectedFromTime(event.target.value);
    };

    const handleChangeToDate = (event) => {
        setSelectedToDate(event.target.value);
    };

    const handleChangeToTime = (event) => {
        setSelectedToTime(event.target.value);
    };

    const handleChangeDeletingAllWithSameTime = () => {
        setDeletingAllWithSameTime(prev => {
            const next = !prev;
            toast.warning(`Changed to deleting ${next ? 'all samples with same time' : 'one sample'}`);
            return next;
        });
    };

    const handleDeleteAction = (index, toastId) => {
        setDeleteSampleIndex(index);
        toast.dismiss(toastId);
    };

    const handleDeleteCancel = (toastId) => {
        toast.warning(
            'Operation cancelled!',
            {
                id: toastId,
                action: null,
                cancel: null,
                duration: 5000
            }
        );
    };

    const handleDeleteSample = (sampleIndex) => {
        const message = deletingAllWithSameTime
            ? 'Delete sample and all samples with same time from all parameters?'
            : 'Delete this sample from database?';
        const toastId = toast.warning(message, {
            style: { width: '20rem' },
            action: <FcOk onClick={() => handleDeleteAction(sampleIndex, toastId)} style={{ fontSize: '4rem' }} />,
            cancel: <MdCancel onClick={() => handleDeleteCancel(toastId)} style={{ fontSize: '4rem', color: 'red' }} />,
            duration: 10000
        });
    };

    useEffect(() => {
        if (deleteSampleIndex === null) {
            return;
        }

        const performDelete = async () => {
            const sample = samples[deleteSampleIndex];
            if (!sample) {
                setDeleteSampleIndex(null);
                return;
            }

            const filteredFlowMeterId = flowMeters
                .filter(
                    flowmeter => flowmeter.location_id.place === selectedPlace &&
                        flowmeter.location_id.thoroughfare === selectedThoroughfare && 
                        flowmeter.location_id.type_line === selectedTypeLine)
                .map((flowmeter) => (flowmeter.id))[0];

            if (!filteredFlowMeterId) {
                toast.error('Error! Select a valid flow meter.');
                setDeleteSampleIndex(null);
                return;
            }

            setLoadingState(true);
            const baseDeleteParams = { sample_time: sample.date };
            const deleteParams = deletingAllWithSameTime
                ? baseDeleteParams
                : (sample.track_number != null ? { ...baseDeleteParams, track_number: sample.track_number } : baseDeleteParams);
            try {
                if (deletingAllWithSameTime) {
                    const parametersToDelete = chartOrder.filter(
                        (parameter) => !excludedSampleDeleteParameters.includes(parameter)
                    );
                    let successCount = 0;
                    for (const parameter of parametersToDelete) {
                        try {
                            const response = await api.delete(
                                `/sample/${filteredFlowMeterId}/${parameter}`,
                                {
                                    withCredentials: true,
                                    params: baseDeleteParams
                                }
                            );
                            if (response.data.success) {
                                successCount++;
                            } else {
                                console.warn(`Failed to delete ${parameter}: ${response.data.message}`);
                            }
                        } catch (err) {
                            console.warn(`Error deleting ${parameter}:`, err.response || err);
                        }
                    }
                    setSamples((prev) => prev.filter((_, index) => index !== deleteSampleIndex));
                    toast.success(`Deleted sample and ${successCount} parameter variations successfully!`);
                } else {
                    const response = await api.delete(
                        `/sample/${filteredFlowMeterId}/${chartName}`,
                        {
                            withCredentials: true,
                            params: deleteParams
                        }
                    );
                    if (response.data.success) {
                        toast.success('Sample deleted successfully!');
                        setSamples((prev) => prev.filter((_, index) => index !== deleteSampleIndex));
                    } else {
                        toast.error(response.data.message || 'Error deleting sample!');
                    }
                }
            } catch (err) {
                console.log(err.response || err);
                toast.error('Unable to delete sample.');
            }
            setLoadingState(false);
            setDeleteSampleIndex(null);
        };

        performDelete();
    }, [deleteSampleIndex]);

    const handleFindButton = () => {
        const areDatesAndTimesSet = selectedFromDate && selectedFromTime && selectedToDate && selectedToTime;
        if (!areDatesAndTimesSet) {
            toast.error('Error! Insert all dates and times!');
            return;
        }

        const dateTimeFromBeforeParsing = `${selectedFromDate}T${selectedFromTime}`;
        const dateTimeToBeforeParsing = `${selectedToDate}T${selectedToTime}`;
        const dateTimeFromParsed = parseISO(dateTimeFromBeforeParsing);
        const dateTimeToParsed = parseISO(dateTimeToBeforeParsing);

        if (!isBefore(dateTimeFromParsed, dateTimeToParsed)) {
            toast.error('Error! First datetime is not earlier!');
            return;
        }

        const diffMs = differenceInMilliseconds(dateTimeToParsed, dateTimeFromParsed);
        const maxRangeSamplesMin = Number(
            process.env.REACT_APP_MAX_FLOW_MEASURMENT_SAMPLES_MIN_RANGE
        );

        const plural = maxRangeSamplesMin === 1 ? '' : 's';

        if (diffMs > maxRangeSamplesMin * 60_000) {
            toast.error(
                `Error! Time window cannot exceed ${maxRangeSamplesMin} minute${plural}.`
            );
            return;
        }

        const filteredFlowMeterId = flowMeters
            .filter(
                flowmeter => flowmeter.location_id.place === selectedPlace &&
                    flowmeter.location_id.thoroughfare === selectedThoroughfare && 
                    flowmeter.location_id.type_line === selectedTypeLine)
            .map((flowmeter) => (flowmeter.id))[0];

        if (!filteredFlowMeterId) {
            toast.error('Error! Select a valid flow meter.');
            return;
        }

        const fetchSamples = async () => {
            setLoadingState(true);
            try {
                const response = await api.get(
                    `/samples/${filteredFlowMeterId}/${chartName}/${dateTimeFromBeforeParsing}/${dateTimeToBeforeParsing}`,
                    { withCredentials: true }
                );
                if (response.data.success) {
                    setSamples(response.data.payload);
                } else {
                    toast.error(response.data.message || 'Error fetching samples!');
                }
            } catch (err) {
                console.log(err.response || err);
                toast.error('Unable to fetch sample data.');
            }
            setLoadingState(false);
        };
        fetchSamples();
    };

    useEffect(() => {
        const fetchFlowMeters = async () => {
            try {
                const response = await api.get('flow-meters', { withCredentials: true });
                if (response.data.success) {
                    setFlowMeters(response.data.payload);
                } else {
                    console.error('Error:', response.data.message);
                }
            } catch (err) {
                console.log(err.response || err);
            }
        };
        fetchFlowMeters();
    }, []);

    useEffect(() => {
        const handlePlaces = () => {
            const newplaces = [...new Set(flowMeters.map(item => item.location_id.place))];
            return newplaces.map((name, index) => ({ id: index + 1, name: name }));
        };
        if (flowMeters.length > 0) {
            setPlaces(handlePlaces());
        }
    }, [flowMeters]);

    useEffect(() => {
        const filterPlaces = () => {
            const newthoroughfare = flowMeters
                .filter(flowmeter => flowmeter.location_id.place === selectedPlace)
                .map((fl, index) => ({ id: index + 1, name: fl.location_id.thoroughfare }));
            return newthoroughfare.reduce((acc, current) => {
                if (!acc.some(item => item.name === current.name)) {
                    acc.push(current);
                }
                return acc;
            }, []);
        };
        if (flowMeters.length > 0) {
            setThoroughfares(filterPlaces());
        }
    }, [selectedPlace]);

    useEffect(() => {
        const filterPlacesAndThoroughfares = () => {
            const newTypeLines = flowMeters
                .filter(flowmeter => flowmeter.location_id.place === selectedPlace && flowmeter.location_id.thoroughfare === selectedThoroughfare)
                .map((fl, index) => ({ id: index + 1, name: fl.location_id.type_line }));
            return newTypeLines.reduce((acc, current) => {
                if (!acc.some(item => item.name === current.name)) {
                    acc.push(current);
                }
                return acc;
            }, []);
        };
        if (flowMeters.length > 0) {
            setTypeLines(filterPlacesAndThoroughfares());
        }
    }, [selectedThoroughfare]);

    return (
        <div className="flow-measurement-record-container">
            <TitleRecordFunction
                addRecordMessage={""}
                deleteRecordMessage={""}
                title='Flow Measurement Samples'
                isViewRecord={true}
            />
            <div className="flow-measurement-record-card">
                <div className="choice-input-group">
                    <label className='bold-label'>Place:</label>
                    <SelectPlace
                        places={places}
                        selectedPlace={selectedPlace}
                        setSelectedPlace={setSelectedPlace}
                    />
                </div>
                <div className="choice-input-group">
                    <label className='bold-label'>Thoroughfare:</label>
                    <SelectThoroughfare
                        thoroughfares={thoroughfares}
                        selectedThoroughfare={selectedThoroughfare}
                        setSelectedThoroughfare={setSelectedThoroughfare}
                    />
                </div>
                <div className="choice-input-group">
                    <label className='bold-label'>Type Line:</label>
                    <SelectTypeLines
                        LocationTypeLines={typeLines}
                        selectedlocationTypeLine={selectedTypeLine}
                        setSelectedLocationTypeLine={setSelectedTypeLine}
                    />
                </div>
                <div className="choice-input-group">
                    <label className='bold-label'>Param:</label>
                    <SelectParameter
                        Parameters={chartOrder}
                        selectedParameter={chartName}
                        setSelectedParameter={setChartName}
                    />
                </div>
                <div className="choice-input-group">
                    <label className='bold-label'>FROM:</label>
                    <div className="choice-date-time-group">
                        <DateInput
                            selectedDate={selectedFromDate}
                            handleSelectedDate={handleChangeFromDate}
                        />
                        <TimeInput
                            selectedTime={selectedFromTime}
                            handleSelectedTime={handleChangeFromTime}
                        />
                    </div>
                </div>
                <div className="choice-input-group">
                    <label className='bold-label'>TO:</label>
                    <div className="choice-date-time-group">
                        <DateInput
                            selectedDate={selectedToDate}
                            handleSelectedDate={handleChangeToDate}
                        />
                        <TimeInput
                            selectedTime={selectedToTime}
                            handleSelectedTime={handleChangeToTime}
                        />
                    </div>
                </div>
                {!loadingState ? <button className='menu-button' onClick={handleFindButton}>
                    Find samples
                </button> : <button className='menu-button'>
                    <Spinner size="small" />
                </button>}
                <p className='range-hint'>Max range: 5 minutes.</p>
            </div>
            <button
                className='flow-measurement-record-button-changing'
                onClick={handleChangeDeletingAllWithSameTime}
            >
                Change to deleting {deletingAllWithSameTime ? 'one sample': 'all samples with same time'}
            </button>
            {!loadingState ? (
                <div className="table-wrapper">
                    {samples.length > 0 ? (
                        <table className="flow-measurement-record-table-style-sample text-align-center">
                            <thead>
                                <tr>
                                    <th>Date / Time</th>
                                    <th>Track</th>
                                    <th>Value</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {samples.map((sample, index) => (
                                    <tr key={`${sample.date}-${index}`}>
                                        <td>{formatSampleDate(sample.date+"Z")}</td>
                                        <td>{sample.track_number ?? '-'}</td>
                                        <td>{String(sample.value)}</td>
                                        <td>
                                            <FaTrashCan
                                                className='manage-database-delete-icon'
                                                role='button'
                                                tabIndex={0}
                                                onClick={() => handleDeleteSample(index)}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter') handleDeleteSample(index);
                                                }}
                                                title='Delete sample'
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p style={{ textAlign: 'center', marginTop: '1rem' }}>No samples loaded. Use the filter above to find data.</p>
                    )}
                </div>
            ) : null}
            <BackButton navigateTo='/manage-database/flow-measurement/record' />
        </div>
    );
};

export default FlowMeasurementSampleRecord;
